import type {
  CcpPlan,
  CompetitorPlan,
  FieldUpdatePayload,
  FieldUpdateSuggestion,
  PlanGenerateInput,
} from "../types"

import { api } from "@/lib/api-client"

/**
 * API client for the Customer Centric Plan (CCP) and Competitor Plan surfaces.
 * The axios instance already prefixes `/api`, so paths begin at `/segment-analysis`.
 *
 * Endpoint map (issue #2471):
 *   GET    …/companies/:companyId/ccp
 *   POST   …/ccp/generate                     { area?, sourceDocIds? }
 *   PATCH  …/ccp/fields/:fieldKey             (Accept / manual Save — persists)
 *   POST   …/ccp/fields/:fieldKey/update      (scoped suggestion — does NOT persist)
 *   GET    …/competitor-plan
 *   POST   …/competitor-plan/generate
 *   PATCH  …/competitor-plan/fields/:fieldKey
 *   POST   …/competitor-plan/fields/:fieldKey/update   (400 for nolatoRelationships)
 *   PUT    …/competitor-plan/fields/nolatoRelationships (manual §06 Save)
 */

function planBase(segmentId: string, companyId: string): string {
  return `/segment-analysis/segments/${encodeURIComponent(segmentId)}/companies/${encodeURIComponent(companyId)}`
}

// ── CCP ───────────────────────────────────────────────────────────────────────

export function getCcp(segmentId: string, companyId: string): Promise<CcpPlan> {
  return api.get<never, CcpPlan>(`${planBase(segmentId, companyId)}/ccp`)
}

export function generateCcp(
  segmentId: string,
  companyId: string,
  input: PlanGenerateInput = {}
): Promise<CcpPlan> {
  return api.post<PlanGenerateInput, CcpPlan>(
    `${planBase(segmentId, companyId)}/ccp/generate`,
    input
  )
}

export function patchCcpField(
  segmentId: string,
  companyId: string,
  edit: { fieldKey: string; payload: FieldUpdatePayload }
): Promise<CcpPlan> {
  return api.patch<FieldUpdatePayload, CcpPlan>(
    `${planBase(segmentId, companyId)}/ccp/fields/${encodeURIComponent(edit.fieldKey)}`,
    edit.payload
  )
}

export function requestCcpFieldUpdate(
  segmentId: string,
  companyId: string,
  update: { fieldKey: string; input?: PlanGenerateInput }
): Promise<FieldUpdateSuggestion> {
  return api.post<PlanGenerateInput, FieldUpdateSuggestion>(
    `${planBase(segmentId, companyId)}/ccp/fields/${encodeURIComponent(update.fieldKey)}/update`,
    update.input ?? {}
  )
}

// ── Competitor Plan ─────────────────────────────────────────────────────────

export function getCompetitorPlan(segmentId: string, companyId: string): Promise<CompetitorPlan> {
  return api.get<never, CompetitorPlan>(`${planBase(segmentId, companyId)}/competitor-plan`)
}

export function generateCompetitorPlan(
  segmentId: string,
  companyId: string,
  input: PlanGenerateInput = {}
): Promise<CompetitorPlan> {
  return api.post<PlanGenerateInput, CompetitorPlan>(
    `${planBase(segmentId, companyId)}/competitor-plan/generate`,
    input
  )
}

export function patchCompetitorField(
  segmentId: string,
  companyId: string,
  edit: { fieldKey: string; payload: FieldUpdatePayload }
): Promise<CompetitorPlan> {
  return api.patch<FieldUpdatePayload, CompetitorPlan>(
    `${planBase(segmentId, companyId)}/competitor-plan/fields/${encodeURIComponent(edit.fieldKey)}`,
    edit.payload
  )
}

export function requestCompetitorFieldUpdate(
  segmentId: string,
  companyId: string,
  update: { fieldKey: string; input?: PlanGenerateInput }
): Promise<FieldUpdateSuggestion> {
  return api.post<PlanGenerateInput, FieldUpdateSuggestion>(
    `${planBase(segmentId, companyId)}/competitor-plan/fields/${encodeURIComponent(update.fieldKey)}/update`,
    update.input ?? {}
  )
}

/** Manual §06 Save — the only write path for `nolatoRelationships` (dedicated PUT). */
export function saveNolatoRelationships(
  segmentId: string,
  companyId: string,
  payload: FieldUpdatePayload
): Promise<CompetitorPlan> {
  return api.put<FieldUpdatePayload, CompetitorPlan>(
    `${planBase(segmentId, companyId)}/competitor-plan/fields/nolatoRelationships`,
    payload
  )
}
